"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { track } from "@/lib/directory-tracking";
import { classifyPageType } from "./PageViewTracking";

// Scroll depth por página, en cortes de 25 %.
//
// Distingue dos rebotes que hoy se ven iguales: el que abre el home y
// cierra sin mirar nada (llega a 0-25 %) del que recorre todo y aun así
// no clickea (llega a 100 %). El primero es un problema del anuncio o
// de la velocidad; el segundo, de la oferta que estamos mostrando.
//
// Vive a nivel layout para cubrir home y ficha con una sola
// implementación — antes esta lógica estaba dentro de ComercioAnalytics
// y solo corría en la ficha, que es justamente donde NO está el
// problema.

const MARCAS = [25, 50, 75, 100] as const;

export default function ScrollDepthTracking(): null {
  const pathname = usePathname();
  const enviadas = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Reset por navegación: cada página tiene su propio recorrido.
    enviadas.current = new Set();

    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      // Páginas que entran completas en el viewport no generan scroll y
      // no deben contarse como "llegó al 100 %".
      if (total <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / total) * 100));
      for (const marca of MARCAS) {
        if (pct >= marca && !enviadas.current.has(marca)) {
          enviadas.current.add(marca);
          track("scroll_depth", {
            pageType: classifyPageType(pathname ?? "/"),
            path: pathname ?? undefined,
            dedupKey: `${pathname}:${marca}`,
            metadata: { percent: marca },
          });
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return null;
}
